import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";

import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort, formatEuroAmount } from "../lib/currency";

const numFmt = new Intl.NumberFormat("fr-FR");

function StatCard({ label, value, subtext, testId, tone = "default" }) {
  const toneClass = tone === "accent" ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border ${toneClass} p-5 shadow-sm`} data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900" data-testid={`${testId}-value`}>{value}</p>
      {subtext && <p className="mt-1 text-xs text-slate-500">{subtext}</p>}
    </div>
  );
}

function StatusBar({ status, count, total, testId }) {
  const pct = total > 0 ? Math.max(4, (count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm" data-testid={testId}>
      <span className="w-32 text-slate-600 capitalize">{status.replace("_", " ")}</span>
      <div className="flex-1 overflow-hidden rounded-full bg-slate-100">
        <div className="h-2 bg-indigo-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right text-xs font-semibold text-slate-700">{count}</span>
    </div>
  );
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/overview", { params: refresh ? { refresh: true } : {} });
      setData(data);
    } catch (err) {
      // silent; page still renders empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalSubs = useMemo(() => {
    if (!data) return 0;
    return Object.values(data.submissions_by_status || {}).reduce((a, b) => a + b, 0);
  }, [data]);

  const locale = i18n.language;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_dash.eyebrow")}</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl" data-testid="admin-dash-title">{t("admin_dash.title")}</h1>
            {data && (
              <p className="mt-2 text-xs text-slate-500" data-testid="admin-dash-generated-at">
                {t("admin_dash.last_updated")}: {formatDateShort(data.generated_at)} · {new Date(data.generated_at).toLocaleTimeString(locale === "fr" ? "fr-FR" : "en-US")}
              </p>
            )}
          </div>
          <button
            onClick={() => load(true)}
            disabled={loading}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            data-testid="admin-dash-refresh"
          >
            {loading ? t("common.loading") : t("admin_dash.refresh")}
          </button>
        </div>

        {!data ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">{t("common.loading")}</div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
              <StatCard label={t("admin_dash.stat_users")} value={numFmt.format(data.total_users)} subtext={t("admin_dash.stat_users_sub", { emp: data.users_by_role?.employer ?? 0, ag: data.users_by_role?.agency ?? 0 })} testId="stat-users" />
              <StatCard label={t("admin_dash.stat_orgs")} value={numFmt.format(data.total_orgs)} subtext={t("admin_dash.stat_orgs_sub", { emp: data.orgs_by_type?.employer ?? 0, ag: data.orgs_by_type?.agency ?? 0 })} testId="stat-orgs" />
              <StatCard label={t("admin_dash.stat_active_subs")} value={numFmt.format(data.active_subscriptions)} testId="stat-active-subs" tone="accent" />
              <StatCard label={t("admin_dash.stat_trials_active")} value={numFmt.format(data.trials_active || 0)} testId="stat-trials-active" tone="accent" />
              <StatCard
                label={t("admin_dash.stat_promo")}
                value={data.promo_active ? t("admin_dash.promo_active_value", { days: data.promo_days_remaining || 0 }) : t("admin_dash.promo_off")}
                testId="stat-promo"
                tone={data.promo_active ? "accent" : "default"}
              />
              <StatCard label={t("admin_dash.stat_mrr")} value={formatEuroAmount(data.mrr_eur, locale)} testId="stat-mrr" tone="accent" />
              <StatCard label={t("admin_dash.stat_active_missions")} value={numFmt.format(data.active_missions)} subtext={t("admin_dash.stat_missions_sub", { total: numFmt.format(data.total_missions_posted) })} testId="stat-active-missions" />
              <StatCard label={t("admin_dash.stat_hires_30d")} value={numFmt.format(data.hires_last_30d)} testId="stat-hires" />
              <StatCard label={t("admin_dash.stat_signups_30d")} value={numFmt.format((data.new_signups_last_30d?.employer ?? 0) + (data.new_signups_last_30d?.agency ?? 0))} subtext={t("admin_dash.stat_signups_sub", { emp: data.new_signups_last_30d?.employer ?? 0, ag: data.new_signups_last_30d?.agency ?? 0 })} testId="stat-signups" />
              <StatCard label={t("admin_dash.stat_ttfs")} value={data.avg_time_to_first_submission_days !== null && data.avg_time_to_first_submission_days !== undefined ? `${data.avg_time_to_first_submission_days.toFixed(1)}${t("admin_dash.days_short")}` : "—"} testId="stat-ttfs" />
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="submissions-bars">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("admin_dash.subs_by_status")}</h2>
                <div className="mt-4 space-y-2">
                  {Object.entries(data.submissions_by_status || {}).map(([k, v]) => (
                    <StatusBar key={k} status={k} count={v} total={totalSubs} testId={`subs-bar-${k}`} />
                  ))}
                  {totalSubs === 0 && <p className="text-sm text-slate-500">{t("admin_dash.subs_empty")}</p>}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="orgs-by-status">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("admin_dash.orgs_by_status")}</h2>
                <div className="mt-4 space-y-2">
                  {Object.entries(data.orgs_by_status || {}).map(([k, v]) => (
                    <StatusBar key={k} status={k} count={v} total={data.total_orgs} testId={`orgs-bar-${k}`} />
                  ))}
                </div>
              </section>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <TopTable title={t("admin_dash.top_by_subs")} rows={data.top_5_agencies_by_submissions} testId="top-subs" />
              <TopTable title={t("admin_dash.top_by_hires")} rows={data.top_5_agencies_by_hires} testId="top-hires" />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function TopTable({ title, rows, testId }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={testId}>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
      {!rows || rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{t("admin_dash.empty")}</p>
      ) : (
        <ol className="mt-3 space-y-2 text-sm">
          {rows.map((r, i) => (
            <li key={r.org_id} className="flex items-center gap-3" data-testid={`${testId}-row-${r.org_id}`}>
              <span className="w-6 text-right text-xs text-slate-400">{i + 1}</span>
              <span className="flex-1 text-slate-800">{r.name}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">{r.count}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
